// Builds the 210's panel layout: flat header panels (Invoice details,
// Dates, Parties & addresses, Totals) the same way sections845.ts does,
// plus a repeating Stops panel whose each group is one LX stop — its own
// G62 dates, L5 description and L0 weight detail as entries, its own Name
// loop(s) flattened in (mirroring sections856.ts's buildHLGroup856, which
// flattens a node's nameLoops into its entries the same way), and one
// subgroup per L1 freight/accessorial charge line (entries-only, no further
// nesting — the 210 never nests deeper than Stop > charge line).

import type {Header210, NameLoop210, Parsed210, StopLoop210} from './parser210';
import {entriesOf} from './sections';
import type {Section, SectionEntry, SectionGroup} from './sections';

type CommonSectionKey210 = 'order' | 'dates' | 'parties' | 'stops' | 'totals';

const SECTION_ORDER_210: CommonSectionKey210[] = [
  'order',
  'dates',
  'parties',
  'stops',
  'totals',
];

type HeaderKey210 = Exclude<keyof Header210, 'nameLoops'>;

const HEADER_SECTION_210: Record<HeaderKey210, CommonSectionKey210> = {
  b3: 'order',
  c3: 'order',
  itd: 'order',
  refs: 'order',
  g62: 'dates',
  other: 'order',
};

const SECTION_META_210: Record<
  CommonSectionKey210,
  {title: string; description: string}
> = {
  order: {
    title: 'Invoice details',
    description:
      "The carrier's invoice (B3), currency (C3) and terms (ITD) if present, and header reference numbers.",
  },
  dates: {
    title: 'Dates',
    description:
      'Header-level dates (G62), each tagged by its own date qualifier.',
  },
  parties: {
    title: 'Parties & addresses',
    description: 'Who is involved and where, at the header level.',
  },
  stops: {
    title: 'Stops & freight charges',
    description:
      "Each stop in the shipment's itinerary, with its own parties, dates, description, weight detail and freight/accessorial charge lines.",
  },
  totals: {
    title: 'Totals',
    description:
      'Total weight (with its qualifier) and the total charge across every stop.',
  },
};

const NAME_LOOP_KEYS_210: ReadonlyArray<keyof NameLoop210> = [
  'n1',
  'n2',
  'n3',
  'n4',
  'per',
  'other',
];

const STOP_KEYS_210: ReadonlyArray<
  Exclude<keyof StopLoop210, 'nameLoops' | 'l1'>
> = ['lx', 'g62', 'l5', 'l0', 'other'];

function buildStopGroup210(stop: StopLoop210, path: string): SectionGroup {
  const entries = entriesOf(stop, STOP_KEYS_210, path);
  stop.nameLoops.forEach((loop, i) => {
    entries.push(
      ...entriesOf(loop, NAME_LOOP_KEYS_210, `${path}.nameLoops[${i}]`)
    );
  });
  const subgroups: SectionGroup[] = stop.l1.map((l1, i) => ({
    key: `${path}.l1[${i}]`,
    entries: [{segment: l1, basePath: `${path}.l1[${i}]`}],
  }));
  return {
    key: path,
    entries,
    subgroups: subgroups.length > 0 ? subgroups : undefined,
  };
}

/** Builds the data-type sections for one 210 transaction: the flat header
 * panels, a repeating Stops panel (each group one LX stop, nesting its own
 * charge lines as subgroups), and a totals panel (L3) — omitting any panel
 * with nothing in it. */
export function buildSections210(parsed: Parsed210, txPath: string): Section[] {
  const flat: Record<CommonSectionKey210, SectionEntry[]> = {
    order: [],
    dates: [],
    parties: [],
    stops: [],
    totals: [],
  };
  const headerKeys = Object.keys(HEADER_SECTION_210) as HeaderKey210[];
  for (const key of headerKeys) {
    flat[HEADER_SECTION_210[key]].push(
      ...entriesOf(parsed.header, [key], `${txPath}.header`)
    );
  }
  flat.totals.push(...entriesOf(parsed.summary, ['l3'], `${txPath}.summary`));

  const partyGroups: SectionGroup[] = parsed.header.nameLoops.map((loop, i) => {
    const path = `${txPath}.header.nameLoops[${i}]`;
    return {key: path, entries: entriesOf(loop, NAME_LOOP_KEYS_210, path)};
  });

  const stopGroups: SectionGroup[] = parsed.stops.map((stop, i) =>
    buildStopGroup210(stop, `${txPath}.stops[${i}]`)
  );

  return SECTION_ORDER_210.flatMap((key): Section[] => {
    const repeating = key === 'parties' || key === 'stops';
    const groups = repeating
      ? key === 'parties'
        ? partyGroups
        : stopGroups
      : flat[key].length > 0
        ? [{key, entries: flat[key]}]
        : [];
    if (groups.length === 0) return [];
    return [{key, ...SECTION_META_210[key], repeating, groups}];
  });
}
